import type { Card } from '@campfire/cards';

/** Poker hand categories, weakest first. */
export const HAND_NAMES = ['High card', 'One pair', 'Two pair', 'Three of a kind', 'Straight', 'Flush', 'Full house', 'Four of a kind', 'Straight flush'] as const;

export interface HandValue {
  /** Category index into HAND_NAMES, then tie-breaking ranks, highest first (2..14). */
  readonly score: readonly number[];
  readonly name: (typeof HAND_NAMES)[number];
  readonly cards: readonly Card[];
}

const VALUE: Record<string, number> = { '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, J: 11, Q: 12, K: 13, A: 14 };
const value = (c: Card) => VALUE[c.rank] ?? 0;

/** Highest card of a straight in these distinct values (wheel A-2-3-4-5 counts as 5), or 0. */
function straightHigh(values: readonly number[]): number {
  const set = new Set(values);
  if (set.size !== 5) return 0;
  const sorted = [...set].sort((a, b) => b - a);
  if ((sorted[0] as number) - (sorted[4] as number) === 4) return sorted[0] as number;
  return sorted.join() === '14,5,4,3,2' ? 5 : 0;
}

/** Score exactly five cards. */
export function score5(cards: readonly Card[]): readonly number[] {
  const values = cards.map(value);
  const flush = cards.every((c) => c.suit === cards[0]?.suit);
  const high = straightHigh(values);
  const counts = new Map<number, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  // Groups ordered by size then rank: e.g. full house 8s over 3s → [8, 3].
  const groups = [...counts.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]);
  const shape = groups.map((g) => g[1]).join('');
  const ranks = groups.map((g) => g[0]);
  if (high && flush) return [8, high];
  if (shape === '41') return [7, ...ranks];
  if (shape === '32') return [6, ...ranks];
  if (flush) return [5, ...[...values].sort((a, b) => b - a)];
  if (high) return [4, high];
  if (shape === '311') return [3, ...ranks];
  if (shape === '221') return [2, ...ranks];
  if (shape === '2111') return [1, ...ranks];
  return [0, ...ranks];
}

export function compareScores(a: readonly number[], b: readonly number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/** Best five-card hand from any 5–7 cards. */
export function bestHand(cards: readonly Card[]): HandValue {
  let best: { score: readonly number[]; cards: Card[] } | null = null;
  const n = cards.length;
  const pick = (start: number, chosen: Card[]) => {
    if (chosen.length === 5) {
      const score = score5(chosen);
      if (!best || compareScores(score, best.score) > 0) best = { score, cards: [...chosen] };
      return;
    }
    for (let i = start; i <= n - (5 - chosen.length); i++) pick(i + 1, [...chosen, cards[i] as Card]);
  };
  pick(0, []);
  const b = best as unknown as { score: readonly number[]; cards: Card[] };
  return { score: b.score, name: HAND_NAMES[b.score[0] as number] as HandValue['name'], cards: b.cards };
}
